import i18n from '@/lib/i18n';
import enLabels from '../locales/en/labels.json';
import usLabels from '../locales/en-US/labels.json';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { getEnvConfig } from '@/lib/env-config';

/**
 * Region-aware UI copy. `en/labels.json` is the British default and the full
 * base list, so its keys are the authoritative set; the typed accessor below
 * only accepts those keys. `en-US/labels.json` holds American overrides and can
 * be filled out gradually — any missing key falls back to the British default.
 */
export type LabelKey = keyof typeof enLabels;

// Compile-time guard: every en-US key must be a valid default key. If the US
// file gains a key the British base lacks (typo / stale key), this won't compile.
type UsKeysAreSubset = keyof typeof usLabels extends LabelKey ? true : never;
const _assertUsSubset: UsKeysAreSubset = true;
void _assertUsSubset;

/**
 * Region used for copy when there is no matter in scope. Falls back to the
 * Azure deployment region: the `productionus` deployment is the only US region,
 * everything else (UK deployments, non-prod, local) is treated as British.
 * Safe to call before the runtime env config has loaded — it defaults to UK.
 */
function deploymentRegion(): string {
  try {
    return getEnvConfig().environment === 'productionus' ? 'US' : 'UK';
  } catch {
    return 'UK';
  }
}

/**
 * Returns a `label(key)` accessor that resolves region-specific copy.
 *
 * Region resolves in priority order: an explicit override (e.g. MatterSettings,
 * where copy must follow the live form selection), then the current matter's
 * region, then the Azure deployment region, then British. Missing US keys fall
 * back to the British default automatically via i18next `fallbackLng`.
 */
export function useMatterLabels(
  regionOverride?: string | null,
): (key: LabelKey, options?: Record<string, unknown>) => string {
  const { matterRegion } = useMatterJurisdiction();
  const region = regionOverride ?? matterRegion ?? deploymentRegion();
  const lng = region?.toUpperCase() === 'US' ? 'en-US' : 'en';
  const t = i18n.getFixedT(lng, 'labels');
  return (key: LabelKey, options?: Record<string, unknown>) =>
    (options ? t(key as never, options as never) : t(key)) as string;
}

/** Forum-appropriate document types for Case Overview empty / generating copy.

US matters have no litigation/arbitration forum (`forum_enabled: false`), so
they always take the default keys — complaints / orders / motions.
*/
export function overviewDocumentSuggestions(
  label: (key: LabelKey, options?: Record<string, unknown>) => string,
  forum?: string | null,
): { doc1: string; doc2: string; doc3: string } {
  if (forum === 'Arbitration') {
    return {
      doc1: label('overview-doc-arb-1'),
      doc2: label('overview-doc-arb-2'),
      doc3: label('overview-doc-arb-3'),
    };
  }
  return {
    doc1: label('overview-doc-1'),
    doc2: label('overview-doc-2'),
    doc3: label('overview-doc-3'),
  };
}
